import { de } from '../../i18n'
import type { AccountListItem } from './types'

export type UserSortField = 'name' | 'email' | 'companyName' | 'role' | 'createdAt' | 'lastActiveAt' | 'isAdmin'
export type SortDirection = 'asc' | 'desc'

export interface UserColumnDef {
  field: UserSortField
  label: string
  sortable: boolean
  align: 'left' | 'right'
}

/** §6's column set, in the spec's own order: Name, E-Mail, Firma, Rolle, registriert am, zuletzt aktiv, Administrator. */
export const USER_COLUMNS: UserColumnDef[] = [
  { field: 'name', label: de.users.columns.name, sortable: true, align: 'left' },
  { field: 'email', label: de.users.columns.email, sortable: true, align: 'left' },
  { field: 'companyName', label: de.users.columns.company, sortable: true, align: 'left' },
  { field: 'role', label: de.users.columns.role, sortable: true, align: 'left' },
  { field: 'createdAt', label: de.users.columns.createdAt, sortable: true, align: 'left' },
  { field: 'lastActiveAt', label: de.users.columns.lastActiveAt, sortable: true, align: 'left' },
  { field: 'isAdmin', label: de.users.columns.isAdmin, sortable: true, align: 'left' },
]

function fullName(account: AccountListItem): string {
  return `${account.firstName} ${account.lastName}`
}

/** The quick-search box: matches name, e-mail or company, case-insensitively. */
export function matchesSearch(account: AccountListItem, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    fullName(account).toLowerCase().includes(q) ||
    account.email.toLowerCase().includes(q) ||
    account.companyName.toLowerCase().includes(q)
  )
}

export interface UserFilters {
  role: 'owner' | 'member' | null
  isAdmin: boolean | null
}

export function matchesFilters(account: AccountListItem, filters: UserFilters): boolean {
  if (filters.role !== null && account.role !== filters.role) return false
  if (filters.isAdmin !== null && account.isAdmin !== filters.isAdmin) return false
  return true
}

function compareValue(account: AccountListItem, field: UserSortField): string | number {
  switch (field) {
    case 'name': return fullName(account).toLowerCase()
    case 'email': return account.email.toLowerCase()
    case 'companyName': return account.companyName.toLowerCase()
    case 'role': return account.role
    case 'createdAt': return account.createdAt
    // Accounts that never signed in sort as if they were oldest — "never
    // active" is the least recent state a "zuletzt aktiv" column can show.
    case 'lastActiveAt': return account.lastActiveAt ?? ''
    case 'isAdmin': return account.isAdmin ? 1 : 0
  }
}

export function sortAccounts(accounts: AccountListItem[], field: UserSortField, dir: SortDirection): AccountListItem[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...accounts].sort((a, b) => {
    const va = compareValue(a, field)
    const vb = compareValue(b, field)
    if (va < vb) return -1 * sign
    if (va > vb) return 1 * sign
    return 0
  })
}

/** Search, then filter, then sort — in that order, since none of the three depends on the others' output. */
export function selectAccounts(
  accounts: AccountListItem[],
  options: { search: string; filters: UserFilters; sort: UserSortField; dir: SortDirection },
): AccountListItem[] {
  const filtered = accounts
    .filter((account) => matchesSearch(account, options.search))
    .filter((account) => matchesFilters(account, options.filters))
  return sortAccounts(filtered, options.sort, options.dir)
}
